/**
 * Members Tab
 * Displays all community members with search filtering and action menu
 */

import { EllipsisVertical, Loader2, MessageCircle, Search, User, X } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { BlurGate, GuestAction } from "@/components/guest"
import {
  CheckCircleIcon,
  HeartFilledIcon,
  HeartOutlineIcon,
  ShieldCheckIcon,
  UserMinusIcon,
  UserProfileIcon,
  WarningIcon,
  XCircleIcon,
} from "@/components/icons"
import { ReportUserModal } from "@/components/shared/ReportUserModal"
import { Avatar } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { LoginModal } from "@/features/auth/components/LoginModal"
import { useCreateDmConversation } from "@/features/chats/hooks/useDmChat"
import { useFollowActions } from "@/features/profile/hooks/useFollowActions"
import { useDebounce } from "@/hooks/useDebounce"
import { useTranslation } from "@/language"
import { useJoinRequestActions, useJoinRequests } from "../hooks/useCommunityMembership"
import { useCommunityMembersInfinite } from "../hooks/useCommunityMembersInfinite"
import { useRemoveMember } from "../hooks/useRemoveMember"
import type { CommunityMember } from "../services/community.service"
import type { CommunityDetail } from "../types/community-detail"
import { AssignRoleModal } from "./AssignRoleModal"
import { InviteModal } from "./InviteModal"

type MemberSubtab = "all" | "members" | "moderators" | "owners" | "hidden" | "requests"

interface MembersTabProps {
  communityId: string
  searchQuery?: string
  permissions?: CommunityDetail["permissions"]
  currentUserId?: string
  creatorId?: string
  showSearch?: boolean
  onToggleSearch?: () => void
  onSearchChange?: (value: string) => void
  isGuest?: boolean
}

export function MembersTab({
  communityId,
  searchQuery = "",
  permissions,
  currentUserId,
  creatorId,
  showSearch,
  onToggleSearch,
  onSearchChange,
  isGuest = false,
}: MembersTabProps) {
  const { t } = useTranslation("communities")
  const navigate = useNavigate()

  // Debounce search query to avoid API calls on every keystroke
  const debouncedSearch = useDebounce(searchQuery, 500)

  // Active subtab state
  const [activeSubtab, setActiveSubtab] = useState<MemberSubtab>("all")

  // Menu open state
  const [openMenuId, setOpenMenuId] = useState<string | null>(null)
  const menuRefs = useRef<Map<string, HTMLDivElement>>(new Map())

  // Local state to track follow status changes immediately
  const [followStatusMap, setFollowStatusMap] = useState<Record<string, boolean>>({})

  // Report Modal state
  const [reportMember, setReportMember] = useState<CommunityMember | null>(null)

  // Assign Role Modal state
  const [isAssignRoleModalOpen, setIsAssignRoleModalOpen] = useState(false)
  const [selectedMemberForRole, setSelectedMemberForRole] = useState<CommunityMember | null>(null)

  // Invite Modal state
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false)
  const [showLoginModal, setShowLoginModal] = useState(false)

  // Determine role filter based on active subtab
  const roleFilter =
    activeSubtab === "members"
      ? "member"
      : activeSubtab === "moderators"
        ? "moderator"
        : activeSubtab === "owners"
          ? "owner"
          : undefined

  // Fetch members with infinite scroll (only for all, members, moderators, owners tabs)
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage, refetch } =
    useCommunityMembersInfinite(communityId, {
      role: roleFilter,
      searchQuery: debouncedSearch || undefined,
    })

  // Get counts from meta
  const totalMembersCount = data?.pages?.[0]?.meta?.totalCount ?? 0
  const memberCount = data?.pages?.[0]?.meta?.MemberCount ?? 0
  const moderatorCount = data?.pages?.[0]?.meta?.ModeratorCount ?? 0
  const ownerCount = data?.pages?.[0]?.meta?.OwnerCount ?? 0
  const hiddenCount = data?.pages?.[0]?.meta?.HiddenCount ?? 0
  const requestCount = data?.pages?.[0]?.meta?.requestCount ?? 0

  // Fetch join requests for requests tab
  const { requests: joinRequests, isLoading: isLoadingRequests } = useJoinRequests(
    communityId,
    undefined,
    activeSubtab === "requests",
  )

  // Accept/reject join request mutations
  const { acceptRequest, rejectRequest, isAccepting, isRejecting } = useJoinRequestActions()

  // Handlers for accept/reject
  const handleAcceptRequest = (requestId: string) => {
    acceptRequest({ communityId, requestId })
  }

  const handleRejectRequest = (requestId: string) => {
    rejectRequest({ communityId, requestId })
  }

  // Follow/unfollow mutation
  const { follow, unfollow, isFollowing, isUnfollowing } = useFollowActions()

  // Remove member mutation
  const { mutate: removeMember, isPending: isRemoving } = useRemoveMember({ communityId })

  // Create or get DM conversation
  const { mutate: createConversation, isPending: isCreatingConversation } =
    useCreateDmConversation()

  // Get all members from all pages
  const allMembers = data?.pages.flatMap(page => page.members) ?? []

  // Server-side search is now used via API - no local filtering needed
  const filteredMembers = allMembers

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const clickedAnyMenu = Array.from(menuRefs.current.values()).some(el =>
        el?.contains(event.target as Node),
      )
      if (!clickedAnyMenu) {
        setOpenMenuId(null)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const handleProfileClick = (e: React.MouseEvent, memberId: string) => {
    e.stopPropagation()
    setOpenMenuId(null)
    navigate(`/profile/${memberId}`)
  }

  const handleFollowToggle = (e: React.MouseEvent, member: CommunityMember) => {
    e.stopPropagation()
    const currentStatus =
      followStatusMap[member.id] !== undefined ? followStatusMap[member.id] : member.isFollowing

    setFollowStatusMap(prev => ({
      ...prev,
      [member.id]: !currentStatus,
    }))

    if (currentStatus) {
      unfollow(member.id)
    } else {
      follow(member.id)
    }
  }

  const handleRemoveMember = (e: React.MouseEvent, memberId: string) => {
    e.stopPropagation()
    setOpenMenuId(null)
    removeMember(memberId)
  }

  const handleMenuClick = (e: React.MouseEvent, memberId: string) => {
    e.stopPropagation()
    setOpenMenuId(openMenuId === memberId ? null : memberId)
  }

  const handleAssignRole = (e: React.MouseEvent, member: CommunityMember) => {
    e.stopPropagation()
    setOpenMenuId(null)
    setSelectedMemberForRole(member)
    setIsAssignRoleModalOpen(true)
  }

  const handleReport = (e: React.MouseEvent, member: CommunityMember) => {
    e.stopPropagation()
    setOpenMenuId(null)
    setReportMember(member)
  }

  return (
    <div className="md:py-10 md:px-1 min-h-[300px]">
      <div className="flex items-center justify-between md:justify-start gap-4 mb-2.5 md:mb-6 h-10 px-5 md:px-0">
        <h2 className="text-lg font-semibold text-primary leading-7.5 uppercase">
          {t("tabs.members")}
        </h2>

        <div className="flex flex-col md:flex-row md:flex-1 items-center justify-between">
          {/* Sub-tab Navigation */}
          <div className="hidden md:flex gap-1 md:border-l-1 mb-5 md:mb-0">
            <button
              type="button"
              className={`px-4 py-1 text-sm font-medium transition-colors cursor-pointer ${
                activeSubtab === "all" ? "text-primary" : "text-body"
              }`}
              onClick={() => setActiveSubtab("all")}
            >
              All ({totalMembersCount})
            </button>
            <button
              type="button"
              className={`px-4 py-1 text-sm font-medium transition-colors cursor-pointer ${
                activeSubtab === "members" ? "text-primary" : "text-body"
              }`}
              onClick={() => setActiveSubtab("members")}
            >
              Members ({memberCount})
            </button>
            <button
              type="button"
              className={`px-4 py-1 text-sm font-medium transition-colors cursor-pointer ${
                activeSubtab === "moderators" ? "text-primary" : "text-body"
              }`}
              onClick={() => setActiveSubtab("moderators")}
            >
              Moderators ({moderatorCount})
            </button>
            <button
              type="button"
              className={`px-4 py-1 text-sm font-medium transition-colors cursor-pointer ${
                activeSubtab === "owners" ? "text-primary" : "text-body"
              }`}
              onClick={() => setActiveSubtab("owners")}
            >
              Owners ({ownerCount})
            </button>
            {permissions?.canManageMembers && (
              <>
                <button
                  type="button"
                  className={`px-4 py-1 text-sm font-medium transition-colors cursor-pointer ${
                    activeSubtab === "hidden" ? "text-primary" : "text-body"
                  }`}
                  onClick={() => setActiveSubtab("hidden")}
                >
                  Hidden ({hiddenCount})
                </button>
                <button
                  type="button"
                  className={`px-4 py-1 text-sm font-medium transition-colors cursor-pointer ${
                    activeSubtab === "requests" ? "text-primary" : "text-body"
                  }`}
                  onClick={() => setActiveSubtab("requests")}
                >
                  Requests ({requestCount})
                </button>
              </>
            )}
          </div>

          <div className="flex items-center gap-3">
            {/* Mobile search toggle */}
            <div className="md:hidden flex items-center">
              {showSearch ? (
                <div className="flex items-center gap-2 rounded-md border border-card bg-card px-3 py-1.5">
                  <Search className="w-4 h-4 text-ash shrink-0" />
                  <Input
                    type="text"
                    placeholder={t("searchPlaceholder")}
                    value={searchQuery}
                    onChange={e => onSearchChange?.(e.target.value)}
                    className="max-w-40 h-5 border-none bg-transparent px-2 text-sm shadow-none focus-visible:ring-0"
                    autoFocus
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="shrink-0 h-6 w-6 text-ash"
                    onClick={onToggleSearch}
                    aria-label="Close search"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <button
                  type="button"
                  className="opacity-50 cursor-pointer"
                  onClick={() => (isGuest ? setShowLoginModal(true) : onToggleSearch?.())}
                  aria-label="Search"
                >
                  <Search className="w-5 h-5 text-charcoal" />
                </button>
              )}
            </div>
            {/* Add ritualist button - only for moderators */}
            {permissions?.canManageMembers && (
              <Button
                variant="primary"
                size="md"
                className="hidden md:flex items-center gap-2 px-15 py-3 uppercase"
                onClick={() => setIsInviteModalOpen(true)}
              >
                {"Invite Member"}
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="md:hidden flex gap-5 md:border-l mb-5 md:mb-0 px-5 md:px-0 overflow-x-auto whitespace-nowrap scrollbar-hide">
        <button
          type="button"
          className={`py-1 text-sm font-semibold transition-colors cursor-pointer shrink-0 ${
            activeSubtab === "all" ? "text-primary" : "text-body"
          }`}
          onClick={() => setActiveSubtab("all")}
        >
          All ({totalMembersCount})
        </button>
        <button
          type="button"
          className={`py-1 text-sm font-semibold transition-colors cursor-pointer shrink-0 ${
            activeSubtab === "members" ? "text-primary" : "text-body"
          }`}
          onClick={() => setActiveSubtab("members")}
        >
          Members ({memberCount})
        </button>
        <button
          type="button"
          className={`py-1 text-sm font-semibold transition-colors cursor-pointer shrink-0 ${
            activeSubtab === "moderators" ? "text-primary" : "text-body"
          }`}
          onClick={() => setActiveSubtab("moderators")}
        >
          Moderators ({moderatorCount})
        </button>
        <button
          type="button"
          className={`py-1 text-sm font-semibold transition-colors cursor-pointer shrink-0 ${
            activeSubtab === "owners" ? "text-primary" : "text-body"
          }`}
          onClick={() => setActiveSubtab("owners")}
        >
          Owners ({ownerCount})
        </button>
        {permissions?.canManageMembers && (
          <>
            <button
              type="button"
              className={`py-1 text-sm font-semibold transition-colors cursor-pointer shrink-0 ${
                activeSubtab === "hidden" ? "text-primary" : "text-body"
              }`}
              onClick={() => setActiveSubtab("hidden")}
            >
              Hidden ({hiddenCount})
            </button>
            <button
              type="button"
              className={`py-1 text-sm font-semibold transition-colors cursor-pointer shrink-0 ${
                activeSubtab === "requests" ? "text-primary" : "text-body"
              }`}
              onClick={() => setActiveSubtab("requests")}
            >
              Requests ({requestCount})
            </button>
          </>
        )}
      </div>

      {permissions?.canManageMembers && (
        <Button
          variant="outline"
          size="md"
          className="md:hidden flex mb-5 items-center gap-2 px-15 py-3 uppercase w-full border-[#F2F0EB] text-[#F2F0EB]"
          onClick={() => setIsInviteModalOpen(true)}
        >
          {"Invite Member"}
        </Button>
      )}

      {/* Invite Modal */}
      <InviteModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        communityId={communityId}
        roleType="member"
        onSuccess={() => refetch()}
      />

      {/* Content based on active subtab */}
      {/* Loading State */}
      {(isLoading || isLoadingRequests) && (
        <div className="mt-4 flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Hidden tab - empty state */}
      {!isLoading && !isLoadingRequests && activeSubtab === "hidden" && (
        <div className="mt-10 flex flex-col items-center justify-center py-12">
          <User className="mb-2 h-12 w-12 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">No hidden members</p>
        </div>
      )}

      {/* Requests tab */}
      {!isLoading && !isLoadingRequests && activeSubtab === "requests" && (
        <div className="mt-10">
          {joinRequests.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <User className="mb-2 h-12 w-12 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">No pending requests</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
              {joinRequests.map(request => (
                <div
                  key={request.id}
                  className="flex items-center justify-between rounded-xl border border-card bg-card p-4"
                >
                  <div className="flex items-center gap-3">
                    <Avatar
                      src={request.requesterAvatar ?? undefined}
                      fallback={request.requesterName || "?"}
                      size="lg"
                      className="h-12 w-12"
                    />
                    <div>
                      <p className="text-sm font-medium">{request.requesterName}</p>
                      <p className="text-xs text-muted-foreground">
                        {request.requesterUsername ? `@${request.requesterUsername}` : ""}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <button
                      className="!p-0"
                      onClick={() => handleRejectRequest(request.id)}
                      disabled={isAccepting || isRejecting}
                    >
                      <XCircleIcon width={25} height={25} />
                    </button>
                    <button
                      className="!p-0"
                      onClick={() => handleAcceptRequest(request.id)}
                      disabled={isAccepting || isRejecting}
                    >
                      <CheckCircleIcon width={25} height={25} />
                    </button>
                    <button className="!p-0 cursor-pointer" disabled={isAccepting || isRejecting}>
                      <EllipsisVertical className="size-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Members, Moderators, All tabs */}
      {!isLoading &&
        !isLoadingRequests &&
        ["all", "members", "moderators", "owners"].includes(activeSubtab) && (
          <div className="md:mt-10">
            {filteredMembers.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <User className="mb-2 h-12 w-12 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  {searchQuery ? t("tabs.noMembersFound") : t("tabs.noMembers")}
                </p>
              </div>
            ) : isGuest ? (
              <BlurGate ctaVariant="followers">
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 md:gap-5">
                  {filteredMembers.map(member => (
                    <div
                      key={member.id}
                      className="relative flex items-center gap-3 md:rounded-xl border-t last:border-b md:border border-[#2A2A2A] md:border-card md:bg-card px-5 py-3.75 transition-colors hover:bg-muted/50 md:p-5"
                    >
                      <div
                        className="cursor-pointer"
                        onClick={e => handleProfileClick(e, member.id)}
                      >
                        <Avatar
                          src={member.coverImage ?? undefined}
                          fallback={member.name || "?"}
                          size="lg"
                          className="w-10 h-10 md:h-14 md:w-14"
                        />
                      </div>
                      <div className="">
                        <p
                          className="truncate text-sm font-medium cursor-pointer"
                          onClick={e => handleProfileClick(e, member.id)}
                        >
                          {member.name || "No name"}
                        </p>
                        <p
                          className="truncate text-xs text-muted-foreground cursor-pointer"
                          onClick={e => handleProfileClick(e, member.id)}
                        >
                          {member.username ? `@${member.username}` : "No username"}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </BlurGate>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 md:gap-5">
                  {filteredMembers.map(member => (
                    <div
                      key={member.id}
                      className="relative flex items-center gap-3 md:rounded-xl border-t last:border-b md:border border-[#2A2A2A] md:border-card md:bg-card px-5 py-3.75 transition-colors hover:bg-muted/50 md:p-5"
                    >
                      {/* Avatar */}
                      <div
                        className="cursor-pointer"
                        onClick={e => handleProfileClick(e, member.id)}
                      >
                        <Avatar
                          src={member.coverImage ?? undefined}
                          fallback={member.name || "?"}
                          size="lg"
                          className="w-10 h-10 md:h-14 md:w-14"
                        />
                      </div>

                      {/* User Info */}
                      <div className="">
                        <p
                          className="w-35 truncate text-sm font-medium cursor-pointer"
                          onClick={e => handleProfileClick(e, member.id)}
                        >
                          {member.name || "No name"}
                        </p>
                        <p
                          className="truncate text-xs text-muted-foreground cursor-pointer"
                          onClick={e => handleProfileClick(e, member.id)}
                        >
                          {member.username ? `@${member.username}` : "No username"}
                        </p>
                      </div>

                      {/* Action buttons - always visible on right side */}
                      {/* Only show for non-current user */}
                      {member.id !== currentUserId && (
                        <div className="flex-1 flex items-center justify-end gap-1">
                          {/* Chat icon */}
                          {isGuest ? (
                            <GuestAction>
                              <button
                                type="button"
                                className="p-1.5 rounded-full hover:bg-muted/50 cursor-pointer"
                                title="Chat"
                              >
                                <MessageCircle className="h-5 w-5 text-muted-foreground" />
                              </button>
                            </GuestAction>
                          ) : (
                            <button
                              type="button"
                              className="p-1.5 rounded-full hover:bg-muted/50 cursor-pointer"
                              onClick={e => {
                                e.stopPropagation()
                                createConversation(member.id, {
                                  onSuccess: conversation => {
                                    navigate(`/chats/dm/${conversation.id}`)
                                  },
                                })
                              }}
                              disabled={isCreatingConversation}
                              title="Chat"
                            >
                              <MessageCircle className="h-5 w-5 text-muted-foreground" />
                            </button>
                          )}

                          {/* Follow button */}
                          {isGuest ? (
                            <GuestAction>
                              <button
                                type="button"
                                className="p-1.5 rounded-full hover:bg-muted/50 cursor-pointer"
                                title="Follow"
                              >
                                <HeartOutlineIcon width={21} height={21} color="#F2F0EB" />
                              </button>
                            </GuestAction>
                          ) : (
                            <button
                              type="button"
                              className="p-1.5 rounded-full hover:bg-muted/50 cursor-pointer"
                              onClick={e => handleFollowToggle(e, member)}
                              disabled={isFollowing || isUnfollowing}
                              title={
                                followStatusMap[member.id] !== undefined
                                  ? followStatusMap[member.id]
                                    ? "Unfollow"
                                    : "Follow"
                                  : member.isFollowing
                                    ? "Unfollow"
                                    : "Follow"
                              }
                            >
                              {(
                                followStatusMap[member.id] !== undefined
                                  ? followStatusMap[member.id]
                                  : member.isFollowing
                              ) ? (
                                <HeartFilledIcon width={21} height={21} color="#8d8e63" />
                              ) : (
                                <HeartOutlineIcon width={21} height={21} color="#F2F0EB" />
                              )}
                            </button>
                          )}

                          {/* Menu button */}
                          <div
                            className="relative"
                            ref={el => {
                              if (el) menuRefs.current.set(member.id, el)
                              else menuRefs.current.delete(member.id)
                            }}
                          >
                            <button
                              type="button"
                              className="p-1.5 rounded-full hover:bg-muted/50 cursor-pointer"
                              onClick={e => handleMenuClick(e, member.id)}
                            >
                              <EllipsisVertical className="h-5 w-5 text-muted-foreground" />
                            </button>

                            {/* Dropdown Menu */}
                            {openMenuId === member.id && (
                              <div className="absolute right-0 p-4 bottom-10 w-80 bg-popover text-popover-foreground rounded-lg shadow-lg border border-border overflow-visible z-50 space-y-0">
                                <button
                                  type="button"
                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-muted/50 transition-colors cursor-pointer"
                                  onClick={e => handleProfileClick(e, member.id)}
                                >
                                  <UserProfileIcon width={16} height={20} color="#F2F0EB" />
                                  View Profile
                                </button>

                                {permissions?.canManageMembers &&
                                  creatorId &&
                                  member.id !== creatorId && (
                                    <button
                                      type="button"
                                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-muted/50 transition-colors cursor-pointer"
                                      onClick={e => handleAssignRole(e, member)}
                                    >
                                      <ShieldCheckIcon width={21} height={21} color="#F2F0EB" />
                                      Assign Role
                                    </button>
                                  )}

                                <button
                                  type="button"
                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-brand-terracotta transition-colors cursor-pointer"
                                  onClick={e => handleReport(e, member)}
                                >
                                  <WarningIcon width={18} height={19} color="#C86757" />
                                  Report
                                </button>

                                {permissions?.canManageMembers &&
                                  creatorId &&
                                  member.id !== creatorId && (
                                    <button
                                      type="button"
                                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-destructive text-brand-terracotta transition-colors cursor-pointer"
                                      onClick={e => handleRemoveMember(e, member.id)}
                                      disabled={isRemoving}
                                    >
                                      <UserMinusIcon width={20} height={10} color="#C86757" />
                                      Remove from Community
                                    </button>
                                  )}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Load More Button - only if not filtering */}
                {!searchQuery && hasNextPage && (
                  <div className="py-4 text-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => fetchNextPage()}
                      disabled={isFetchingNextPage}
                    >
                      {isFetchingNextPage ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        "Load more"
                      )}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

      {/* Assign Role Modal */}
      <AssignRoleModal
        isOpen={isAssignRoleModalOpen}
        onClose={() => {
          setIsAssignRoleModalOpen(false)
          setSelectedMemberForRole(null)
        }}
        member={selectedMemberForRole}
        communityId={communityId}
        isOwner={permissions?.isOwner ?? false}
        onSuccess={() => {
          // The useCommunityMembersInfinite hook will automatically refetch
          // No additional action needed
        }}
      />

      {reportMember && (
        <ReportUserModal
          open={!!reportMember}
          onOpenChange={open => {
            if (!open) setReportMember(null)
          }}
          user={{
            id: reportMember.id,
            name: reportMember.name || "User",
            username: reportMember.username || "",
            profilePictureUrl: reportMember.coverImage,
          }}
        />
      )}
      <LoginModal open={showLoginModal} onOpenChange={setShowLoginModal} />
    </div>
  )
}
